import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";

import "./Buttons.scss";
import Button from "../../../../components/button/Button";
import useThemeService from "../../../../services/theme-service/ThemeService";
import useModal from "../../../../components/dialog/useModal";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { Accept } from "../../../../components/svg/svgImageList";
import { lastAccessedOrganizationService } from "../../../../services/organization/LastAccessedOrganizationStorage";
import { InvitationRequestActionProps } from "./InvitationRequestActionProps";

export const AcceptInvitationRequestButton = observer(function (
  props: InvitationRequestActionProps
) {
  const { themeClass } = useThemeService();
  const { isVisible, toggleModal } = useModal();
  const { handleErrorInSnackbar } = useErrorHandler();

  const { invitationRequestsStorage } = { ...props };

  const handleConfirmationAction = () => {
    acceptSelectedInvitation();
    toggleModal();
  };

  const reloadPermissionsAndLastAccessedOrganization = useCallback(async () => {
    await lastAccessedOrganizationService.load();
    await permissionsService.reloadPermissions();
  }, []);

  const acceptSelectedInvitation = useCallback(async () => {
    try {
      await invitationRequestsStorage.acceptForSelectedInvitation(
        invitationRequestsStorage.idOfSelectedItem
      );
      await reloadPermissionsAndLastAccessedOrganization();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.collaboration.request.accept");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    invitationRequestsStorage,
    handleErrorInSnackbar,
    reloadPermissionsAndLastAccessedOrganization,
  ]);

  return (
    <>
      <Button
        icon={{
          svg: Accept,
          textKey: "general.accept.collaboration",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-accept-invitation-request"]),
        }}
        disabled={invitationRequestsStorage.isActionButtonDisabled}
        onClick={toggleModal}
      />
      <WarningBeforeAction
        isVisible={isVisible}
        titleKey="MyCollaborationRequestPage.accept.confirmation.title"
        titleContext={{ organizationName: invitationRequestsStorage.selectedOrganizationName }}
        subtitleKey="MyCollaborationRequestPage.accept.confirmation"
        buttonOk={{
          id: "accept-ok",
          textKey: "general.accept.collaboration",
          action: handleConfirmationAction,
        }}
        toggleModal={toggleModal}
      />
    </>
  );
});
